import { Dialog } from 'primereact/dialog';
import { ListBox, ListBoxChangeEvent } from 'primereact/listbox';
import { Accordion, AccordionTab } from 'primereact/accordion';
import React, { useEffect, useMemo, useRef, useState } from "react";

export interface SelectorDialogProps {
    header: string;
    description?: string;

    width: string;

    labelKey: string;
    availableValues: any[];
    selectedValues: any;

    onSelect: (model: any) => void;
    onclose: () => void;
}

// Helper to extract provider from model ID
const getProviderFromModelId = (modelId: string): string | null => {
    if (!modelId) return null;
    const parts = modelId.split('/');
    return parts.length > 1 ? parts[0] : null;
};

export const SelectorDialogDialog: React.FunctionComponent<SelectorDialogProps> = (props: SelectorDialogProps) => {
    const { header, description, labelKey, availableValues: availableModels, selectedValues: selectedModel, onSelect, onclose, width } = props;
    const listBoxRef = useRef<ListBox>(null);
    const [activeIndex, setActiveIndex] = useState<number | null>(null);

    const onSelectionChanged = (event: ListBoxChangeEvent) => {
        onSelect(event.value);
    };

    // Group models by provider if possible
    const groupedModels = useMemo(() => {
        // Check if models have provider format
        const hasProviders = availableModels.some(model => {
            const id = model[labelKey];
            return typeof id === 'string' && id.includes('/');
        });

        if (!hasProviders) {
            return null; // Use flat list if no providers
        }

        // Group by provider
        const groups: { [provider: string]: any[] } = {};
        const ungrouped: any[] = [];

        availableModels.forEach(model => {
            const id = model[labelKey];
            const provider = getProviderFromModelId(id);
            
            if (provider) {
                if (!groups[provider]) {
                    groups[provider] = [];
                }
                groups[provider].push(model);
            } else {
                ungrouped.push(model);
            }
        });

        // Add ungrouped items to "Other" category if needed
        if (ungrouped.length > 0) {
            groups['Other'] = ungrouped;
        }

        return groups;
    }, [availableModels, labelKey]);

    // Determine active provider for the selected model
    useEffect(() => {
        if (groupedModels && selectedModel) {
            const selectedProvider = getProviderFromModelId(selectedModel[labelKey]);
            if (selectedProvider) {
                // Find index of provider in the accordion
                const providers = Object.keys(groupedModels);
                const index = providers.findIndex(p => p === selectedProvider);
                if (index !== -1) {
                    setActiveIndex(index);
                }
            }
        }
    }, [groupedModels, selectedModel, labelKey]);

    // Scroll to selected item
    useEffect(() => {
        setTimeout(() => {
            if (listBoxRef.current) {
                const listBoxEl = listBoxRef.current.getElement();
                const selectedItem = listBoxEl.querySelector('.p-highlight');
                if (selectedItem) {
                    selectedItem.scrollIntoView({ block: 'nearest' });
                }
            }
        }, 100);
    }, []);

    return (
        <Dialog
            className="SelectorDialogDialog"
            header={header}
            visible={true}
            style={{ width: width }}
            onHide={onclose}
            closeOnEscape={true}
        >
            <div className="selector-dialog-content" style={{ display: 'flex', flexDirection: 'column', maxHeight: '70vh' }}>
                {description ? <p className='mt-0'>{description}</p> : null}
                <div style={{ flexGrow: 1, overflow: 'hidden' }}>
                    {groupedModels ? (
                        <Accordion 
                            activeIndex={activeIndex !== null ? activeIndex : undefined} 
                            onTabChange={(e) => setActiveIndex(e.index as number)}
                            className="w-full"
                            style={{ maxHeight: '60vh', overflow: 'auto' }}
                        >
                            {Object.entries(groupedModels).map(([provider, models]) => (
                                <AccordionTab key={provider} header={provider}>
                                    <ListBox
                                        ref={provider === getProviderFromModelId(selectedModel?.[labelKey]) ? listBoxRef : null}
                                        value={selectedModel}
                                        onChange={onSelectionChanged}
                                        options={models}
                                        optionLabel={labelKey}
                                        className="w-full"
                                        style={{ overflow: 'visible' }}
                                        listStyle={{ maxHeight: '30vh', overflow: 'auto' }}
                                    />
                                </AccordionTab>
                            ))}
                        </Accordion>
                    ) : (
                        <ListBox
                            ref={listBoxRef}
                            value={selectedModel}
                            onChange={onSelectionChanged}
                            options={availableModels}
                            optionLabel={labelKey}
                            className="w-full"
                            style={{ maxHeight: '60vh', overflow: 'auto' }}
                            listStyle={{ overflow: 'visible' }}
                        />
                    )}
                </div>
            </div>
        </Dialog>
    );
}

export default SelectorDialogDialog;
